// Centralized, typed runtime config derived from Vite env (import.meta.env.VITE_*).
// Single source of truth for service/gateway base URLs — no hardcoded hosts in components.
// In V2 the gateway path-routes to backend/AI services, so most callers use `apiBaseUrl`;
// the per-service `ai.*` overrides exist for split deployments / local multi-port dev.

const env = import.meta.env;

export const config = {
  /** Gateway / primary API base. Empty string => same-origin (dev proxy). */
  apiBaseUrl: env.VITE_API_BASE_URL || "",
  /** Chat / cover-letter-PDF / document-tips base. Falls back to the gateway, then same-origin.
   *  Must NOT default to a localhost host: the gateway path-routes these same-origin in
   *  test/prod and CI builds the image with an empty VITE_BACKEND_URL, so a localhost default
   *  leaks into the production bundle and the browser blocks it (loopback-address-space CORS). */
  backendUrl: env.VITE_BACKEND_URL || env.VITE_API_BASE_URL || "",
  /** Payment service base (usually proxied through the backend). */
  paymentServiceUrl: env.VITE_PAYMENT_SERVICE_URL || "",
  /** Supabase — anon key only (never service-role in the browser). */
  supabaseUrl: env.VITE_SUPABASE_URL || "",
  supabaseAnonKey: env.VITE_SUPABASE_ANON_KEY || "",
  /** Google OAuth client ID. PUBLIC by design — it ships in the bundle and is not a secret.
   *  Empty = social sign-in is not configured, so the button is never rendered. */
  googleClientId: env.VITE_GOOGLE_CLIENT_ID || "",
  /** V2 per-AI-service overrides; default to the gateway (apiBaseUrl). */
  ai: {
    chat: env.VITE_AI_CHAT_URL || env.VITE_API_BASE_URL || "",
    coverLetter: env.VITE_AI_COVER_LETTER_URL || env.VITE_API_BASE_URL || "",
    passport: env.VITE_AI_PASSPORT_URL || env.VITE_API_BASE_URL || "",
    document: env.VITE_AI_DOCUMENT_URL || env.VITE_API_BASE_URL || "",
  },
} as const;

export type AppConfig = typeof config;

export const isConfigured = {
  supabase: Boolean(config.supabaseUrl && config.supabaseAnonKey),
  google: Boolean(config.googleClientId),
};
